import { FormEvent, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UserRoundCog } from "lucide-react";
import { getAssignableOrgRoleKeys, type OrgRoleName } from "@doculegis/shared";
import { GlassCard } from "@/components/common/GlassCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import {
  authClient,
  listOrgMembers,
  resendOrgMemberVerification,
  setOrgMemberPassword,
  type OrgMemberRecord,
} from "@/lib/auth-client";
import { formatDate } from "@/lib/utils";

export function OrgUserDetailPage() {
  const { userId = "" } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { activeOrganization } = useAuth();
  const { showToast } = useToast();

  const [memberId, setMemberId] = useState<string | null>(null);
  const [member, setMember] = useState<OrgMemberRecord | null>(null);
  const [orgRole, setOrgRole] = useState<string>("member");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [assignableRoles, setAssignableRoles] = useState<OrgRoleName[]>([]);

  const isOwnerMember = member?.role === "owner";
  const orgId = activeOrganization?.id ?? "";

  const loadUser = useCallback(async () => {
    if (!userId || !orgId) return;
    setIsLoading(true);
    setError(null);

    try {
      const { members, enabledOrgRoles } = await listOrgMembers();
      const entry = members.find((m) => m.userId === userId) ?? null;
      if (!entry) {
        setMember(null);
        setMemberId(null);
        setError("User is not a member of this organization");
        return;
      }

      setMemberId(entry.id);
      setMember(entry);
      setOrgRole(entry.role);
      setAssignableRoles(getAssignableOrgRoleKeys(enabledOrgRoles));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load user");
      setMember(null);
      setMemberId(null);
    } finally {
      setIsLoading(false);
    }
  }, [orgId, userId]);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  async function handleSetOrgRole(event: FormEvent) {
    event.preventDefault();
    if (!memberId || !orgId || isOwnerMember) return;
    setError(null);
    setMessage(null);

    const { error: roleError } = await authClient.organization.updateMemberRole({
      memberId,
      role: orgRole,
      organizationId: orgId,
    });
    if (roleError) {
      setError(roleError.message ?? "Failed to update organization role");
      return;
    }
    setMessage("Organization role updated.");
    await loadUser();
  }

  async function handleSetPassword(event: FormEvent) {
    event.preventDefault();
    if (!userId || !newPassword || isOwnerMember) return;
    setError(null);
    setMessage(null);
    setIsUpdatingPassword(true);

    try {
      await setOrgMemberPassword(userId, newPassword);
      setNewPassword("");
      showToast("Password updated.", "success");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to set password";
      showToast(msg, "error");
    } finally {
      setIsUpdatingPassword(false);
    }
  }

  async function handleResendVerification() {
    if (!member?.user.email || !userId) return;
    setError(null);
    setMessage(null);
    setIsResending(true);

    try {
      await resendOrgMemberVerification(userId);
      setMessage("Verification email sent.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resend verification email");
    } finally {
      setIsResending(false);
    }
  }

  async function handleRemove() {
    if (!memberId || !orgId || isOwnerMember) return;
    setIsRemoving(true);
    setError(null);
    setMessage(null);

    const { error: removeError } = await authClient.organization.removeMember({
      memberIdOrEmail: memberId,
      organizationId: orgId,
    });
    setIsRemoving(false);

    if (removeError) {
      setError(removeError.message ?? "Failed to remove user from organization");
      setIsRemoveOpen(false);
      return;
    }
    navigate("/users", { replace: true });
  }

  if (isLoading) {
    return (
      <section className="p-6 md:p-8">
        <p className="text-sm text-slate-300/85">Loading user…</p>
      </section>
    );
  }

  if (!member) {
    return (
      <section className="p-6 md:p-8">
        <div className="rounded-lg border border-red-500/30 bg-red-500/15 px-3 py-2 text-sm text-red-300">
          {error ?? "User not found"}
        </div>
      </section>
    );
  }

  return (
    <section className="p-6 md:p-8">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-[var(--primary-soft-border)] bg-[var(--primary-soft-bg)] text-[#7c3aed] sm:h-10 sm:w-10 dark:border-violet-300/30 dark:bg-violet-500/15 dark:text-violet-200">
          <UserRoundCog className="h-4 w-4 sm:h-5 sm:w-5" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-[var(--lm-foreground-strong)] dark:text-white">{member.user.email}</h1>
          <p className="text-sm text-[var(--lm-muted)] dark:text-slate-300/85">
            {member.user.fullName || member.user.name || "No display name"}
          </p>
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/15 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      ) : null}

      {message ? (
        <div className="mb-4 rounded-lg border border-violet-400/25 bg-violet-500/10 px-3 py-2 text-sm text-violet-200">
          {message}
        </div>
      ) : null}

      <GlassCard className="mb-6 border-[var(--lm-border-card)] bg-white shadow-[var(--shadow-card)] dark:border-glass-border dark:bg-slate-950/45 dark:shadow-none">
        <h2 className="mb-4 text-sm font-semibold text-[var(--lm-foreground-strong)] dark:text-slate-200">Profile</h2>
        <dl className="grid gap-4 text-sm md:grid-cols-2">
          <div>
            <dt className="text-xs text-[var(--lm-muted)] dark:text-slate-400">ID</dt>
            <dd className="mt-0.5 break-all text-[var(--lm-foreground-strong)] dark:text-slate-100">{member.user.id}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--lm-muted)] dark:text-slate-400">Organization role</dt>
            <dd className="mt-0.5 capitalize text-[var(--lm-foreground-strong)] dark:text-slate-100">{member.role}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--lm-muted)] dark:text-slate-400">Verified</dt>
            <dd className="mt-0.5 text-[var(--lm-foreground-strong)] dark:text-slate-100">{member.user.emailVerified ? "Yes" : "No"}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--lm-muted)] dark:text-slate-400">Banned</dt>
            <dd className="mt-0.5 text-[var(--lm-foreground-strong)] dark:text-slate-100">{member.user.banned ? "Yes" : "No"}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--lm-muted)] dark:text-slate-400">Created</dt>
            <dd className="mt-0.5 text-[var(--lm-foreground-strong)] dark:text-slate-100">{formatDate(member.user.createdAt)}</dd>
          </div>
        </dl>
      </GlassCard>

      {isOwnerMember ? (
        <GlassCard className="border-[var(--lm-border-card)] bg-white shadow-[var(--shadow-card)] dark:border-glass-border dark:bg-slate-950/45 dark:shadow-none">
          <p className="text-sm text-[var(--lm-muted)] dark:text-slate-300/85">
            The organization owner account cannot be edited or removed from here.
          </p>
        </GlassCard>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <GlassCard className="border-[var(--lm-border-card)] bg-white shadow-[var(--shadow-card)] dark:border-glass-border dark:bg-slate-950/45 dark:shadow-none">
            <form onSubmit={handleSetOrgRole} className="space-y-4">
              <h2 className="text-sm font-semibold text-[var(--lm-foreground-strong)] dark:text-slate-200">Set organization role</h2>
              <div className="space-y-1.5">
                <Label htmlFor="org-role">Organization role</Label>
                <Select value={orgRole} onValueChange={setOrgRole}>
                  <SelectTrigger id="org-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {assignableRoles.map((role: OrgRoleName) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit">Update organization role</Button>
            </form>
          </GlassCard>

          <GlassCard className="border-[var(--lm-border-card)] bg-white shadow-[var(--shadow-card)] dark:border-glass-border dark:bg-slate-950/45 dark:shadow-none">
            <form onSubmit={handleSetPassword} className="space-y-4">
              <h2 className="text-sm font-semibold text-[var(--lm-foreground-strong)] dark:text-slate-200">Set password</h2>
              <div className="space-y-1.5">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                />
                <p className="text-xs text-slate-400">Minimum 8 characters</p>
              </div>
              <Button type="submit" disabled={isUpdatingPassword}>
                {isUpdatingPassword ? "Updating…" : "Update password"}
              </Button>
            </form>
          </GlassCard>

          {!member.user.emailVerified ? (
            <GlassCard className="border-[var(--lm-border-card)] bg-white shadow-[var(--shadow-card)] dark:border-glass-border dark:bg-slate-950/45 dark:shadow-none">
              <div className="space-y-4">
                <h2 className="text-sm font-semibold text-[var(--lm-foreground-strong)] dark:text-slate-200">Email verification</h2>
                <p className="text-xs text-[var(--lm-muted)] dark:text-slate-400">
                  Send a confirmation email so this user can sign in.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void handleResendVerification()}
                  disabled={isResending}
                >
                  {isResending ? "Sending…" : "Resend verification email"}
                </Button>
              </div>
            </GlassCard>
          ) : null}

          <GlassCard className="border-red-500/30 bg-white shadow-[var(--shadow-card)] dark:bg-slate-950/45 dark:shadow-none lg:col-span-2">
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-red-600 dark:text-red-300">Remove from organization</h2>
              <p className="text-xs text-[var(--lm-muted)] dark:text-slate-400">
                Remove this user from your organization. Their account will remain, but they will
                lose access to your organization&apos;s apps and data.
              </p>
              <Button type="button" variant="destructive" onClick={() => setIsRemoveOpen(true)}>
                Remove user
              </Button>
            </div>
          </GlassCard>
        </div>
      )}

      <Dialog open={isRemoveOpen} onOpenChange={setIsRemoveOpen}>
        <DialogContent className="border-[var(--lm-border-subtle)] bg-white text-[var(--lm-foreground-strong)] dark:border-slate-700 dark:bg-slate-900 dark:text-white">
          <DialogHeader>
            <DialogTitle>Remove user from organization</DialogTitle>
            <DialogDescription className="text-[var(--lm-muted)] dark:text-slate-400">
              Remove <span className="font-medium text-[var(--lm-foreground-strong)] dark:text-slate-200">{member.user.email}</span> from{" "}
              {activeOrganization?.name ?? "this organization"}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsRemoveOpen(false)}
              disabled={isRemoving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleRemove()}
              disabled={isRemoving}
            >
              {isRemoving ? "Removing…" : "Remove user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
